import type { FeatureFlagConfiguration } from "./client";

// Extract the union type of all possible variant values from a flag configuration
export type ExtractVariantType<T> =
  T extends NonNullable<FeatureFlagConfiguration>
    ? T[keyof T]["variants"][keyof T[keyof T]["variants"]]
    : never;

/**
 * Extracts flag key and configuration from the flag config object
 * @param flagConfig - Flag configuration object
 * @returns Tuple of [flagKey, defaultValue]
 */
export function extractFlagConfig<
  T extends NonNullable<FeatureFlagConfiguration>,
>(
  flagConfig: T
): [string, T[keyof T]["variants"][keyof T[keyof T]["variants"]]] {
  const flagEntries = Object.entries(flagConfig);
  const [actualFlagKey, config] = flagEntries[0];

  if (!config) {
    throw new Error(
      `Invalid flag configuration: missing config for ${actualFlagKey}`
    );
  }

  const defaultValue = config.variants[
    config.defaultVariant
  ] as T[keyof T]["variants"][keyof T[keyof T]["variants"]];

  return [actualFlagKey, defaultValue];
}

/**
 * Gets the appropriate flag value based on the default value type
 * @param flagKey - The flag key
 * @param defaultValue - The default value from the flag configuration
 * @param getValue - Function to get the flag value (either from client or hook)
 * @returns The flag value
 */
export function getFlagValue<T>(
  flagKey: string,
  defaultValue: unknown,
  getValue: {
    boolean: (key: string, defaultVal: boolean) => boolean;
    string: (key: string, defaultVal: string) => string;
  }
): T {
  if (typeof defaultValue === "boolean") {
    return getValue.boolean(flagKey, defaultValue) as T;
  }

  if (typeof defaultValue === "string") {
    return getValue.string(flagKey, defaultValue) as T;
  }

  throw new Error(`Unsupported flag type for key: ${flagKey}`);
}
